// src/components/admin/CompatibilityManagement.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import debounce from 'lodash.debounce';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DisabledReason from '../common/DisabledReason';
import CloseIcon from '@mui/icons-material/Close';
import RefreshButton from '../common/RefreshButton';
import useFokusNachLaden from '../common/hooks/useFokusNachLaden';
import TableSkeleton from '../../Skeletons/TableSkeleton';

const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY;
const ADMIN_HEADERS = ADMIN_KEY ? { headers: { 'X-Admin-Key': ADMIN_KEY } } : {};

const COMPATIBILITY_FILTER_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'machine', label: 'Espresso machines' },
  { value: 'grinder', label: 'Grinders' },
  { value: 'dripper', label: 'Drippers' },
];

const COMPATIBILITY_TYPE_OPTIONS = [
  { value: 'machine', label: 'Espresso machines' },
  { value: 'grinder', label: 'Grinders' },
  { value: 'dripper', label: 'Drippers' },
];

const formatCompatibilityType = (value) => {
  const match = COMPATIBILITY_TYPE_OPTIONS.find((opt) => opt.value === value);
  return match ? match.label : value || 'n/a';
};

const formatVariantDetails = (product) => {
  if (!product) return '';
  const parts = [];
  if (product.variant_name) parts.push(product.variant_name);
  if (product.sku) parts.push(`SKU: ${product.sku}`);
  return parts.join(' · ');
};

const formatProductLabel = (product) => {
  if (!product) return '';
  const name = product.product_name || product.product_handle || product.product_id || product.sku;
  const variantDetails = formatVariantDetails(product);
  const suffix = variantDetails ? ` — ${variantDetails}` : '';
  return `${name}${suffix} (${product.product_id})`;
};

const guessCompatibilityType = (product) => {
  const canonical = (product?.canonical_type || '').toLowerCase();
  if (canonical.includes('grinder') || canonical.includes('burr')) return 'grinder';
  if (canonical.includes('dripper') || canonical.includes('filter_paper') || canonical.includes('pour_over') || canonical.includes('brewer')) {
    return 'dripper';
  }
  return 'machine';
};

const truncate = (value, limit = 48) => {
  if (!value) return 'n/a';
  const text = String(value);
  if (text.length <= limit) return text;
  return `${text.slice(0, limit - 1)}…`;
};

const formatProductChipLabel = (product) => {
  if (!product) return 'n/a';
  const name = truncate(product.product_name || product.product_handle || product.product_id);
  const variantDetails = formatVariantDetails(product);
  const suffix = variantDetails ? ` — ${truncate(variantDetails, 48)}` : '';
  const id = product.product_id && name !== product.product_id ? ` (${product.product_id})` : '';
  return `${name}${suffix}${id}`;
};

const getCompatibilityKey = (product) => {
  if (!product) return '';
  return product.variant_id || product.product_id;
};

const buildQuery = (params) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    query.append(key, String(value));
  });
  return query.toString();
};

function SelectProductDialog({ open, onClose, onSelect }) {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');

  const fetchOptions = useCallback(async (term) => {
    setLoading(true);
    setError('');
    try {
      const queryString = buildQuery({ q: term, limit: 20 });
      const { data } = await api.get(`/api/admin/compatibility/products?${queryString}`, ADMIN_HEADERS);
      setOptions(data?.products || []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load products.');
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedFetch = useMemo(() => debounce(fetchOptions, 300), [fetchOptions]);

  useEffect(() => {
    if (!open) return;
    debouncedFetch(query);
    return () => debouncedFetch.cancel();
  }, [open, query, debouncedFetch]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setOptions([]);
      setSelected(null);
      setError('');
    }
  }, [open]);

  const handleSelect = () => {
    if (!selected) return;
    onSelect(selected);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Select product</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Autocomplete
            options={options}
            value={selected}
            inputValue={query}
            onInputChange={(_, value) => setQuery(value)}
            onChange={(_, value) => setSelected(value)}
            getOptionLabel={formatProductLabel}
            isOptionEqualToValue={(option, value) => getCompatibilityKey(option) === getCompatibilityKey(value)}
            filterOptions={(items) => items}
            loading={loading}
            renderOption={(props, option) => {
              const details = formatVariantDetails(option);
              const title = truncate(option.product_name || option.product_handle || option.product_id, 64);
              return (
                <li {...props}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2">{title}</Typography>
                    {details ? (
                      <Typography variant="caption" color="text.secondary">
                        {details}
                      </Typography>
                    ) : null}
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search by name, SKU, or ID"
                placeholder="Start typing to search…"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSelect} variant="contained" disabled={!selected}>
          Select
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CompatibilityEditorDialog({ open, productId, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [product, setProduct] = useState(null);
  const [compatibilityType, setCompatibilityType] = useState('machine');
  const [isExclusive, setIsExclusive] = useState(false);
  const [notes, setNotes] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  // 🔴 K-F1 (2026-10-10): der Speichern-Knopf sperrt sich beim Schreiben und
  // verliert dabei den Fokus — der Haken holt ihn danach zurueck.
  const speichernKnopf = useRef(null);
  useFokusNachLaden(speichernKnopf, saving);

  const resetState = () => {
    setError('');
    setProduct(null);
    setCompatibilityType('machine');
    setIsExclusive(false);
    setNotes('');
    setSelectedProducts([]);
    setSearchInput('');
    setSearchOptions([]);
    setSuggestions([]);
  };

  const loadCompatibility = useCallback(async () => {
    if (!open || !productId) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(`/api/admin/compatibility/${productId}`, ADMIN_HEADERS);
      const productPayload = data?.product || null;
      setProduct(productPayload);
      const existing = (data?.compatibility || [])[0];
      if (existing) {
        setCompatibilityType(existing.compatibility_type || guessCompatibilityType(productPayload));
        setIsExclusive(Boolean(existing.is_exclusive));
        setNotes(existing.notes || '');
        setSelectedProducts(existing.compatible_products || []);
      } else {
        setCompatibilityType(guessCompatibilityType(productPayload));
        setIsExclusive(false);
        setNotes('');
        setSelectedProducts([]);
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load compatibility details.');
    } finally {
      setLoading(false);
    }
  }, [open, productId]);

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    loadCompatibility();
  }, [open, loadCompatibility]);

  const fetchSuggestions = useCallback(async () => {
    if (!open || !productId || !compatibilityType) return;
    setSuggestionsLoading(true);
    try {
      const queryString = buildQuery({ compatibility_type: compatibilityType });
      const { data } = await api.get(`/api/admin/compatibility/suggestions/${productId}?${queryString}`, ADMIN_HEADERS);
      setSuggestions(data?.suggestions || []);
    } catch (err) {
      setSuggestions([]);
    } finally {
      setSuggestionsLoading(false);
    }
  }, [open, productId, compatibilityType]);

  const fetchSearchOptions = useCallback(async (term) => {
    if (!open || !compatibilityType) return;
    setSearchLoading(true);
    try {
      const queryString = buildQuery({ q: term, compatibility_type: compatibilityType, limit: 25 });
      const { data } = await api.get(`/api/admin/compatibility/products?${queryString}`, ADMIN_HEADERS);
      setSearchOptions(data?.products || []);
    } catch (err) {
      setSearchOptions([]);
    } finally {
      setSearchLoading(false);
    }
  }, [open, compatibilityType]);

  const debouncedSearch = useMemo(() => debounce(fetchSearchOptions, 300), [fetchSearchOptions]);

  useEffect(() => {
    if (!open) return;
    debouncedSearch(searchInput);
    return () => debouncedSearch.cancel();
  }, [open, searchInput, debouncedSearch]);

  useEffect(() => {
    if (!open) return;
    setSearchInput('');
    fetchSuggestions();
  }, [open, compatibilityType, fetchSuggestions]);

  const availableOptions = useMemo(() => {
    const selectedIds = new Set((selectedProducts || []).map((item) => getCompatibilityKey(item)));
    return (searchOptions || []).filter((item) => {
      if (item.product_id === productId) return false;
      const key = getCompatibilityKey(item);
      return key && !selectedIds.has(key);
    });
  }, [searchOptions, selectedProducts, productId]);

  const addSuggested = (item) => {
    if (!item) return;
    setSelectedProducts((prev) => {
      if (prev.some((existing) => getCompatibilityKey(existing) === getCompatibilityKey(item))) return prev;
      return [...prev, item];
    });
  };

  const handleSave = async () => {
    if (!productId) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        compatibility_type: compatibilityType,
        is_exclusive: isExclusive,
        compatible_with: isExclusive
          ? selectedProducts.map((item) => ({
            product_id: item.product_id,
            variant_id: item.variant_id || null,
          }))
          : [],
        notes: notes || null,
      };
      await api.post(`/api/admin/compatibility/${productId}`, payload, ADMIN_HEADERS);
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to save compatibility.');
    } finally {
      setSaving(false);
    }
  };

  const restrictionValue = isExclusive ? 'restricted' : 'none';
  const previewText = isExclusive
    ? selectedProducts.length
      ? `This product will ONLY appear in bundles when one of the ${selectedProducts.length} selected items is also present.`
      : 'This product is restricted but has no compatible products selected yet.'
    : 'No restrictions. This product can appear alongside all equipment.';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>Edit compatibility</span>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">Product details</Typography>
                <Typography variant="body2">Name: {product?.product_name || 'n/a'}</Typography>
                {product?.variant_name && (
                  <Typography variant="body2">Variant: {product.variant_name}</Typography>
                )}
                {product?.sku && (
                  <Typography variant="body2">SKU: {product.sku}</Typography>
                )}
                <Typography variant="body2">ID: {product?.product_id || 'n/a'}</Typography>
                <Typography variant="body2">Type: {product?.canonical_type || 'n/a'}</Typography>
                {product?.portafilter_size != null && (
                  <Typography variant="body2">Portafilter: {product.portafilter_size} mm</Typography>
                )}
                {product?.burr_diameter_mm != null && (
                  <Typography variant="body2">Burr diameter: {product.burr_diameter_mm} mm</Typography>
                )}
                {product?.form_factor && (
                  <Typography variant="body2">Form factor: {product.form_factor}</Typography>
                )}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Typography variant="subtitle2">Compatibility settings</Typography>
                <RadioGroup
                  value={restrictionValue}
                  onChange={(event) => setIsExclusive(event.target.value === 'restricted')}
                >
                  <FormControlLabel
                    value="none"
                    control={<Radio size="small" />}
                    label="No restrictions (compatible with all products)"
                  />
                  <FormControlLabel
                    value="restricted"
                    control={<Radio size="small" />}
                    label="Restrict to specific equipment"
                  />
                </RadioGroup>

                <TextField
                  select
                  label="Compatible equipment type"
                  value={compatibilityType}
                  onChange={(event) => {
                    setCompatibilityType(event.target.value);
                    setSelectedProducts([]);
                  }}
                  disabled={!isExclusive}
                >
                  {COMPATIBILITY_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                <Autocomplete
                  multiple
                  options={availableOptions}
                  value={selectedProducts}
                  onChange={(_, value) => setSelectedProducts(value)}
                  inputValue={searchInput}
                  onInputChange={(_, value) => setSearchInput(value)}
                  getOptionLabel={formatProductLabel}
                  isOptionEqualToValue={(option, value) => getCompatibilityKey(option) === getCompatibilityKey(value)}
                  filterOptions={(items) => items}
                  loading={searchLoading}
                  disabled={!isExclusive}
                  renderOption={(props, option) => {
                    const details = formatVariantDetails(option);
                    const title = truncate(option.product_name || option.product_handle || option.product_id, 64);
                    return (
                      <li {...props}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2">{title}</Typography>
                          {details ? (
                            <Typography variant="caption" color="text.secondary">
                              {details}
                            </Typography>
                          ) : null}
                        </Box>
                      </li>
                    );
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={getCompatibilityKey(option) || option.product_id}
                        label={formatProductChipLabel(option)}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Add compatible products"
                      placeholder={isExclusive ? 'Search by name or ID…' : 'Enable restrictions to select products'}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {searchLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {isExclusive
                      ? 'Tip: search and select multiple compatible products.'
                      : 'Selections are disabled until restrictions are enabled.'}
                  </Typography>
                </Box>

                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="subtitle2">Smart suggestions</Typography>
                    {suggestionsLoading && <CircularProgress size={14} />}
                  </Stack>
                  {suggestions.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No suggestions yet.
                    </Typography>
                  ) : (
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {suggestions.map((item) => (
                        <Chip
                          key={item.product_id}
                          label={formatProductChipLabel(item)}
                          onClick={isExclusive ? () => addSuggested(item) : undefined}
                          clickable={isExclusive}
                          variant="outlined"
                          sx={{ mb: 1 }}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>

                <TextField
                  label="Notes (internal)"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  multiline
                  minRows={2}
                />
              </Stack>
            </Paper>

            <Alert severity={isExclusive && selectedProducts.length === 0 ? 'warning' : 'info'}>
              {previewText}
            </Alert>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <DisabledReason
          reason={(saving || loading)
            ? null
            : (isExclusive && !compatibilityType) ? 'Select a compatibility type first.' : null}
        >
          {/* 🔴 K-F1 (2026-10-10): der Lauf zeigt sich am Knopf selbst — Ring
              statt Wortwechsel. `loadingPosition="start"` haelt die
              Beschriftung lesbar (die Vorgabe `center` setzt `color:
              transparent`). Die RUHE-Beschriftung bleibt stehen. */}
          <Button
            ref={speichernKnopf}
            onClick={handleSave}
            variant="contained"
            disabled={saving || loading || (isExclusive && !compatibilityType)}
            loading={saving}
            loadingPosition="start"
            aria-busy={saving || undefined}
          >
            Save compatibility
          </Button>
        </DisabledReason>
      </DialogActions>
    </Dialog>
  );
}

export default function CompatibilityManagement() {
  const [query, setQuery] = useState('');
  const [compatibilityType, setCompatibilityType] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectOpen, setSelectOpen] = useState(false);
  const [editorProductId, setEditorProductId] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const queryString = buildQuery({ q: query, compatibility_type: compatibilityType, limit: 200 });
      const { data } = await api.get(`/api/admin/compatibility?${queryString}`, ADMIN_HEADERS);
      setProducts(data?.products || []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load compatibility rules.');
    } finally {
      setLoading(false);
    }
  }, [query, compatibilityType]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchList();
    }, 300);
    return () => clearTimeout(timeout);
  }, [fetchList]);

  const handleRefresh = () => fetchList();

  const handleEdit = (productId) => {
    setEditorProductId(productId);
  };

  const handleCloseEditor = () => {
    setEditorProductId(null);
  };

  const handleSaved = () => {
    fetchList();
  };

  return (
    <Stack spacing={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack spacing={0.5}>
          <Typography variant="h5">Compatibility management</Typography>
          <Typography variant="body2" color="text.secondary">
            Define explicit compatibility rules for accessories, bundles, and upsells.
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          {/* E8 (2026-10-07): der Kopf dieser Flaeche trug zwei Knopfformen —
              einen beschrifteten `outlined`-Refresh neben „Add compatibility".
              Der Refresh ist jetzt derselbe Baustein wie ueberall; die eine
              gefuellte Aktion bleibt „Add compatibility". */}
          <RefreshButton ariaLabel="Refresh compatibility rules" onClick={handleRefresh} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setSelectOpen(true)}>
            Add compatibility
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
            <TextField
              label="Search products"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by name, SKU, or product ID"
              fullWidth
            />
            <TextField
              select
              label="Filter by equipment type"
              value={compatibilityType}
              onChange={(event) => setCompatibilityType(event.target.value)}
              sx={{ minWidth: 220 }}
            >
              {COMPATIBILITY_FILTER_OPTIONS.map((option) => (
                <MenuItem key={option.value || 'all'} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}

          <Divider />

          {loading ? (
            // K-S (UI-Durchgang 2): Seitenaufbau als Geruest der geladenen
            // Tabelle (5 Spalten), nicht als Kreisel.
            <TableSkeleton framed={false} columns={5} rows={5} ariaLabel="Loading compatibility rules" />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>Canonical type</TableCell>
                  <TableCell>Compatibility</TableCell>
                  <TableCell>Restrictions</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        No compatibility rules found yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  products.map((item) => (
                    <TableRow key={item.product_id} hover>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="subtitle2">
                            {truncate(item.product_name || item.product_handle || item.product_id)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.product_id}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {item.canonical_type || 'n/a'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={formatCompatibilityType(item.compatibility_type)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {item.compatible_count || 0} items
                        </Typography>
                        {item.is_exclusive ? (
                          <Typography variant="caption" color="text.secondary">
                            Exclusive
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          startIcon={<EditOutlinedIcon />}
                          onClick={() => handleEdit(item.product_id)}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </Stack>
      </Paper>

      <SelectProductDialog
        open={selectOpen}
        onClose={() => setSelectOpen(false)}
        onSelect={(product) => {
          setSelectOpen(false);
          if (product?.product_id) {
            setEditorProductId(product.product_id);
          }
        }}
      />

      <CompatibilityEditorDialog
        open={Boolean(editorProductId)}
        productId={editorProductId}
        onClose={handleCloseEditor}
        onSaved={handleSaved}
      />
    </Stack>
  );
}
